"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ListCSVParserService = void 0;
const bson_1 = require("bson");
const papaparse_1 = __importDefault(require("papaparse"));
const __1 = require("..");
const style_variants_enum_1 = require("../models/fields/style-variants.enum");
const hexcode_to_stylevariants_1 = require("../utils/hexcode-to-stylevariants");
const stylevariants_to_hexcode_1 = require("../utils/stylevariants-to-hexcode");
class ListCSVParserService {
    static validateFile(fileName, fileSize) {
        if (!ListCSVParserService.isFileExtensionValid(fileName)) {
            throw new Error('Invalid file type detected.');
        }
        if (!ListCSVParserService.isFileSizeValid(fileSize)) {
            throw new Error(`File limit of ${ListCSVParserService.fileSizeInString} exceeded.`);
        }
        if (ListCSVParserService.isFileEmpty(fileSize)) {
            throw new Error('File is empty.');
        }
    }
    static createRowParser(list, timezone = '') {
        let onValidCallback = (cell) => __awaiter(this, void 0, void 0, function* () { return cell; });
        let onInvalidCallback = (cell) => __awaiter(this, void 0, void 0, function* () { return cell; });
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        let onInvalidRowCallback = () => __awaiter(this, void 0, void 0, function* () { });
        let onInvalidRowStyleCallback = () => __awaiter(this, void 0, void 0, function* () { return style_variants_enum_1.StyleVariants.None; });
        return {
            onValid(callback) {
                onValidCallback = callback;
                return this;
            },
            onInvalid(callback) {
                onInvalidCallback = callback;
                return this;
            },
            onInvalidRow(callback) {
                onInvalidRowCallback = callback;
                return this;
            },
            onInvalidRowStyle(callback) {
                onInvalidRowStyleCallback = callback;
                return this;
            },
            parse(line, rowIndex, colourColumnExists = true) {
                return __awaiter(this, void 0, void 0, function* () {
                    const csvCells = yield ListCSVParserService.splitLine(line);
                    const rowName = csvCells[0];
                    if (rowName.trim() === '') {
                        return onInvalidRowCallback();
                    }
                    const propertiesStartIndex = colourColumnExists ? 2 : 1;
                    // extract this to a separate method
                    const rowStyle = colourColumnExists
                        ? yield ListCSVParserService.getRowStyle({
                            styleCell: csvCells[1],
                            onInvalidRowStyleCallback,
                        })
                        : style_variants_enum_1.StyleVariants.None;
                    const newRow = yield __1.ListGlobalItemHelper.createRow({
                        listId: list._id,
                        rowIndex,
                        rowStyle,
                        value: rowName,
                    });
                    const row = Object.assign(Object.assign({}, newRow), { metadata: Object.assign(Object.assign({}, newRow.metadata), { legacyId: new bson_1.ObjectID() }) });
                    const croppedCsvCells = csvCells.slice(propertiesStartIndex);
                    const cells = yield Promise.all(croppedCsvCells
                        .filter((_, index) => index < list.data.properties.length)
                        .map((csvCell, index) => __awaiter(this, void 0, void 0, function* () {
                        const listProperty = list.data.properties[index];
                        const isCellValid = ListCSVParserService.isCellValid(csvCell, listProperty.model);
                        if (isCellValid) {
                            const cell = yield __1.ListGlobalItemHelper.createCell({
                                rowId: row._id,
                                model: listProperty.model,
                                value: csvCell,
                                propertyId: listProperty._id,
                                timezone,
                            });
                            return onValidCallback(cell, csvCell, listProperty, index);
                        }
                        // This needs to be refactored, currently doing this to bypass error when creating a cell with an invalid value
                        const invalidCell = yield __1.ListGlobalItemHelper.createCell({
                            rowId: row._id,
                            model: __1.GlobalItemModels.TextSimple,
                            value: '',
                            propertyId: listProperty._id,
                        });
                        invalidCell.model = listProperty.model;
                        invalidCell.data =
                            listProperty.model === __1.GlobalItemModels.AttachmentSimple
                                ? { attachments: [] }
                                : { value: csvCell };
                        return onInvalidCallback(invalidCell, csvCell, listProperty, index);
                    })));
                    return {
                        listRow: row,
                        listCells: cells,
                    };
                });
            },
        };
    }
    static processLines(source, lineCallback) {
        let index = 0;
        return new Promise((resolve, reject) => {
            papaparse_1.default.parse(source, {
                transform: (value) => `"${value.trim()}"`,
                step: (result, parser) => {
                    var _a, _b;
                    if (!Array.isArray(result.data)) {
                        return;
                    }
                    if (result.data.length === 1 && result.data[0] === '""') {
                        return;
                    }
                    const parsedLine = result.data.join((_b = (_a = result.meta) === null || _a === void 0 ? void 0 : _a.delimiter) !== null && _b !== void 0 ? _b : ',');
                    lineCallback(parsedLine, index, {
                        abort: parser.abort,
                        pause: parser.pause,
                        resume: parser.resume,
                    });
                    index += 1;
                },
                complete: () => {
                    resolve();
                },
                error: (error) => {
                    reject(error);
                },
            });
        });
    }
    static getAllLines(source) {
        return __awaiter(this, void 0, void 0, function* () {
            const lines = [];
            yield this.processLines(source, (line) => {
                lines.push(line);
            });
            return lines;
        });
    }
    static getRowStyle({ styleCell, onInvalidRowStyleCallback, }) {
        return __awaiter(this, void 0, void 0, function* () {
            let rowStyle;
            rowStyle = ListCSVParserService.parseStyle(styleCell);
            if (!ListCSVParserService.isValidHexCodeCell(styleCell)) {
                rowStyle = yield onInvalidRowStyleCallback(styleCell);
            }
            return rowStyle;
        });
    }
    static splitLine(line) {
        return __awaiter(this, void 0, void 0, function* () {
            const parsedResult = papaparse_1.default.parse(line, { transform: (value) => value.trim() }).data;
            return parsedResult[0];
        });
    }
    static parseStyle(hexCode) {
        if (ListCSVParserService.isValidHexCodeCell(hexCode)) {
            return hexcode_to_stylevariants_1.HexCodeToStyleVariants[hexCode];
        }
        return style_variants_enum_1.StyleVariants.None;
    }
    static getPropertiesStartIndex(colourColumnExists) {
        return colourColumnExists ? 2 : 1;
    }
    static isCellValid(value, model) {
        const modelValidationMap = {
            [__1.GlobalItemModels.NumberSimple]: ListCSVParserService.isValidNumberCell,
            [__1.GlobalItemModels.DateSimple]: ListCSVParserService.isValidDateCell,
            [__1.GlobalItemModels.DateExpiry]: ListCSVParserService.isValidDateCell,
            [__1.GlobalItemModels.TextSimple]: ListCSVParserService.isValidTextCell,
            [__1.GlobalItemModels.AttachmentSimple]: () => false,
            [__1.GlobalItemModels.Container]: () => false,
        };
        return modelValidationMap[model](value);
    }
    static isValidHexCodeCell(value) {
        return ListCSVParserService.validHexCodes.includes(value.toUpperCase());
    }
    static isValidDateCell(value) {
        if (value === '')
            return true;
        try {
            return !!__1.ListGlobalItemHelper.getDateData({ value }).value;
        }
        catch (_a) {
            return false;
        }
    }
    static isValidNumberCell(value) {
        const parsedNumber = Number(value);
        return value === '' || (!Number.isNaN(parsedNumber) && parsedNumber.toString() === value);
    }
    static isValidTextCell() {
        return true;
    }
    static isFileSizeValid(fileSize) {
        return fileSize <= ListCSVParserService.supportedFileSize;
    }
    static isFileEmpty(fileSize) {
        return fileSize === 0;
    }
    static isFileExtensionValid(fileName) {
        const fileExtension = fileName.split('.').pop();
        return fileExtension === 'csv';
    }
    static isListRowLimitValid(rowLength) {
        return rowLength <= ListCSVParserService.maxListRows;
    }
    static fileContainsColourColumn(firstRow) {
        const csvRows = papaparse_1.default.parse(firstRow, { transform: (value) => value.trim() }).data;
        return (Array.isArray(csvRows) && csvRows.length > 0 && csvRows[0].length > 1 && csvRows[0][1].startsWith('#'));
    }
}
exports.ListCSVParserService = ListCSVParserService;
ListCSVParserService.validHexCodes = Object.values(stylevariants_to_hexcode_1.StyleVariantsToHexCode);
ListCSVParserService.supportedFileSize = 10 * 1024 * 1024; // 10MB
ListCSVParserService.fileSizeInString = '10MB';
ListCSVParserService.maxListRows = 200;
